import {
    createApiClient
} from './';
import type {
 AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig
} from 'axios';
import {
    notify, extractServerMessage
} from '../notifier';

const BEX_API_BASE_URL: string = process.env.REACT_APP_BEX_SERVER_URL || '';
const BEX_API_ENDPOINT_PREFIX = '/api';

// Surface the server's error message to the user on a failed BeX call
// (e.g. "subscription not found"). Skips 401 (handled by the auth flow) to
// avoid noise on session expiry, skips the app-engine delete-integrity 409
// (a structured {error:'referenced', referenced_by:[...]} protocol response
// the SafeDeleteDialog renders itself — toasting the raw code 'referenced'
// would duplicate it), and re-rejects so per-call .catch handlers still run.
const isReferencedProtocol = (error: AxiosError): boolean => {
    const data: any = error?.response?.data;
    return error?.response?.status === 409
        && data?.error === 'referenced'
        && Array.isArray(data?.referenced_by);
};

// Auth routes that already toast in their page handlers — skip global notification
// to avoid duplicates (e.g. login 403 shows server + page messages).
const AUTH_ROUTES_WITH_LOCAL_ERRORS = [
    '/auth/login',
    '/auth/signup',
    '/auth/forgot-password',
    '/auth/update-expired-password',
    '/auth/reset-password-with-token',
];

// Routes whose UI fully owns error messaging — skip the global toast to avoid a
// false/duplicate alarm. create-app (bootstrap) recovers from a slow/timed-out
// create by polling for the new app, and surfaces a real failure itself, so a
// global toast would either be a false alarm (during recovery) or a duplicate.
const LOCAL_ERROR_ROUTES = [
    '/app-engine/bootstrap',
    // A2A runs: the test panel renders Mind's error envelope in full, so a
    // global toast would only duplicate it in a lossier form.
    '/assistant/a2a/',
];

const hasLocalErrorHandler = (error: AxiosError): boolean => {
    const url = error?.config?.url ?? '';
    return [...AUTH_ROUTES_WITH_LOCAL_ERRORS, ...LOCAL_ERROR_ROUTES]
        .some((route) => url.includes(route));
};

const onResponseError = (error: AxiosError): Promise<never> => {
    const status = error?.response?.status;
    if (status && status !== 401 && !isReferencedProtocol(error) && !hasLocalErrorHandler(error)) {
        const message = extractServerMessage(error);
        if (message) notify(message, 'danger');
    }
    return Promise.reject(error);
};

/**
 * HTTP client for the BeX API endpoints /  services.
 *
 * @type {ApiClient}
 * @see {@link https://github.com/coolriots/BeX-platform-server/tree/main/src/Routers}
 */
export const bexHttpClient: AxiosInstance = createApiClient(
    BEX_API_BASE_URL,
    BEX_API_ENDPOINT_PREFIX,
    (config: InternalAxiosRequestConfig) => config,
    (response: AxiosResponse) => response,
    (error: AxiosError) => Promise.reject(error),
    onResponseError
);
